import type { FormEvent, JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button, Checkbox, Input, Select } from '@artosera/ui';
import { verifierSlug } from '../../../lib/api.js';
import { devisesProposees, fuseauxProposes } from '../../../lib/reglagesGalerie.js';
import { formeSlugValide, proposerSlug } from '../../../lib/slug.js';
import formStyles from '../../../styles/form.module.css';
import styles from '../Ouvertures.module.css';
import { Champ, EMAIL_VALIDE, descriptionChamp, type PropsEtape } from './commun.js';

/** La valeur, après un court délai : la vérification du slug attend la fin de la frappe. */
function useDiffere<T>(valeur: T, delaiMs: number): T {
  const [differee, setDifferee] = useState(valeur);
  useEffect(() => {
    const minuterie = setTimeout(() => setDifferee(valeur), delaiMs);
    return () => clearTimeout(minuterie);
  }, [valeur, delaiMs]);
  return differee;
}

/** Étape 1 : l'identité de la galerie, son identifiant vérifié en direct, ses réglages. */
export function EtapeGalerie({ dossier, enCours, lectureSeule, sauver }: PropsEtape): JSX.Element {
  const { t } = useTranslation('console');
  const identite = dossier?.identite;
  // L'identifiant se fige avec la galerie : plus de vérification, plus de saisie.
  const fige = dossier?.galleryId != null;

  const [nom, setNom] = useState(identite?.nomCommercial ?? '');
  const [slug, setSlug] = useState(identite?.slug ?? '');
  const [slugTouche, setSlugTouche] = useState((identite?.slug ?? '') !== '');
  const [raisonSociale, setRaisonSociale] = useState(identite?.raisonSociale ?? '');
  const [forme, setForme] = useState(identite?.formeJuridique ?? '');
  const [siren, setSiren] = useState(identite?.siren ?? '');
  const [siret, setSiret] = useState(identite?.siret ?? '');
  const [tva, setTva] = useState(identite?.tvaIntracom ?? '');
  const [ligne1, setLigne1] = useState(identite?.adresse.ligne1 ?? '');
  const [ligne2, setLigne2] = useState(identite?.adresse.ligne2 ?? '');
  const [codePostal, setCodePostal] = useState(identite?.adresse.codePostal ?? '');
  const [ville, setVille] = useState(identite?.adresse.ville ?? '');
  const [pays, setPays] = useState(identite?.adresse.pays ?? 'FR');
  const [langue, setLangue] = useState<'fr' | 'en'>(identite?.langue ?? 'fr');
  const [devise, setDevise] = useState(identite?.devise ?? 'EUR');
  const [fuseau, setFuseau] = useState(identite?.fuseau ?? 'Europe/Paris');
  const [groupe, setGroupe] = useState(dossier?.offre.groupeSlug ?? '');
  const [estTest, setEstTest] = useState(identite?.estTest ?? false);
  const [nomDirection, setNomDirection] = useState(dossier?.equipe.direction?.nom ?? '');
  const [emailDirection, setEmailDirection] = useState(dossier?.equipe.direction?.email ?? '');
  const [erreurs, setErreurs] = useState<Record<string, string>>({});

  const slugCourant = slugTouche ? slug : proposerSlug(nom);
  const slugDiffere = useDiffere(slugCourant, 350);
  const formeOk = formeSlugValide(slugCourant);
  const verification = useQuery({
    queryKey: ['console', 'slug-disponible', slugDiffere],
    queryFn: () => verifierSlug(slugDiffere),
    enabled: !fige && formeSlugValide(slugDiffere),
    staleTime: 0,
    gcTime: 0,
  });

  // L'état affiché du slug : vide, en cours, refusé (avec sa raison) ou libre.
  const enAttente = !fige && formeOk && (slugDiffere !== slugCourant || verification.isFetching);
  const raison = !formeOk
    ? slugCourant === ''
      ? null
      : 'invalide'
    : verification.data && !verification.data.disponible && slugDiffere === slugCourant
      ? (verification.data.raison ?? 'invalide')
      : null;
  const disponible =
    fige ||
    (formeOk &&
      !enAttente &&
      verification.data?.disponible === true &&
      slugDiffere === slugCourant);

  const valider = (): Record<string, string> => {
    const prochaines: Record<string, string> = {};
    if (!nom.trim()) prochaines.nom = t('ouvertures.galerie.erreurs.nom');
    if (!fige && !disponible) prochaines.slug = t('ouvertures.galerie.erreurs.slug');
    if (siren.trim() !== '' && !/^\d{9}$/.test(siren.trim())) {
      prochaines.siren = t('ouvertures.galerie.erreurs.siren');
    }
    if (siret.trim() !== '' && !/^\d{14}$/.test(siret.trim())) {
      prochaines.siret = t('ouvertures.galerie.erreurs.siret');
    }
    if (tva.trim() !== '' && !/^[A-Z]{2}[0-9A-Z]{2,13}$/.test(tva.trim())) {
      prochaines.tva = t('ouvertures.galerie.erreurs.tva');
    }
    if (!/^[A-Z]{2}$/.test(pays.trim())) prochaines.pays = t('ouvertures.galerie.erreurs.pays');
    if (!/^[A-Z]{3}$/.test(devise.trim())) {
      prochaines.devise = t('ouvertures.galerie.erreurs.devise');
    }
    if (estTest) {
      if (!nomDirection.trim()) {
        prochaines.nomDirection = t('ouvertures.galerie.erreurs.nomDirection');
      }
      if (!EMAIL_VALIDE.test(emailDirection.trim())) {
        prochaines.emailDirection = t('ouvertures.galerie.erreurs.emailDirection');
      }
    }
    return prochaines;
  };

  const surEnvoi = (evenement: FormEvent): void => {
    evenement.preventDefault();
    const prochaines = valider();
    setErreurs(prochaines);
    if (Object.keys(prochaines).length > 0) return;
    void sauver(
      {
        identite: {
          nomCommercial: nom.trim(),
          slug: slugCourant,
          raisonSociale: raisonSociale.trim(),
          formeJuridique: forme.trim(),
          siren: siren.trim(),
          siret: siret.trim(),
          tvaIntracom: tva.trim(),
          adresse: {
            ligne1: ligne1.trim(),
            ligne2: ligne2.trim(),
            codePostal: codePostal.trim(),
            ville: ville.trim(),
            pays: pays.trim(),
          },
          langue,
          devise: devise.trim(),
          fuseau: fuseau.trim(),
          estTest,
        },
        offre: { groupeSlug: groupe.trim() === '' ? null : groupe.trim().toLowerCase() },
        ...(estTest
          ? { equipe: { direction: { nom: nomDirection.trim(), email: emailDirection.trim() } } }
          : {}),
      },
      // Un espace de test n'a ni contrat ni équipe : directement la création.
      estTest ? 6 : 2,
    );
  };

  const champ = (id: string, aide: boolean, cle?: string): ReturnType<typeof descriptionChamp> =>
    descriptionChamp(id, { aide, erreur: cle !== undefined && Boolean(erreurs[cle]) });

  return (
    <form className={styles.etape} onSubmit={surEnvoi} noValidate>
      <header>
        <h2>{t('ouvertures.galerie.titre')}</h2>
        <p>{t('ouvertures.galerie.texte')}</p>
      </header>

      <fieldset className={styles.groupe} disabled={lectureSeule}>
        <div className={styles.groupeGrille}>
          <Champ id="ouv-nom" label={t('ouvertures.galerie.nom')} erreur={erreurs.nom}>
            <Input
              id="ouv-nom"
              required
              value={nom}
              onChange={(evenement) => setNom(evenement.target.value)}
              {...champ('ouv-nom', false, 'nom')}
            />
          </Champ>

          <Champ
            id="ouv-slug"
            label={t('ouvertures.galerie.slug')}
            aide={fige ? t('ouvertures.galerie.slugFige') : t('ouvertures.galerie.slugAide')}
            erreur={erreurs.slug}
          >
            <Input
              id="ouv-slug"
              mono
              required
              readOnly={fige}
              value={slugCourant}
              onChange={(evenement) => {
                setSlugTouche(true);
                setSlug(evenement.target.value.toLowerCase());
              }}
              {...champ('ouv-slug', true, 'slug')}
            />
            {!fige && slugCourant !== '' ? (
              <p
                className={`${styles.etatChamp} ${
                  raison ? styles.etatChampKo : disponible ? styles.etatChampOk : ''
                }`}
                role="status"
                data-testid="slug-etat"
              >
                {raison
                  ? t(`ouvertures.galerie.raisons.${raison}`)
                  : disponible
                    ? t('ouvertures.galerie.disponible')
                    : t('ouvertures.galerie.verification')}
              </p>
            ) : null}
          </Champ>

          <div className={`${formStyles.champ} ${formStyles.pleineLargeur}`}>
            <span>{t('ouvertures.galerie.adresseSite')}</span>
            <p className={formStyles.apercu} data-testid="apercu-domaine">
              {slugCourant || <span className={formStyles.pas}>…</span>}
              <span className={formStyles.pas}>.artosera.gallery</span>
            </p>
          </div>
        </div>

        <hr className={styles.separateur} />

        <h3>{t('ouvertures.galerie.identiteLegale')}</h3>
        <div className={styles.groupeGrille}>
          <Champ id="ouv-raison" label={t('ouvertures.galerie.raisonSociale')}>
            <Input
              id="ouv-raison"
              value={raisonSociale}
              onChange={(evenement) => setRaisonSociale(evenement.target.value)}
            />
          </Champ>
          <Champ id="ouv-forme" label={t('ouvertures.galerie.formeJuridique')}>
            <Input
              id="ouv-forme"
              value={forme}
              onChange={(evenement) => setForme(evenement.target.value)}
            />
          </Champ>
          <Champ
            id="ouv-siren"
            label={t('ouvertures.galerie.siren')}
            aide={t('ouvertures.galerie.sirenAide')}
            erreur={erreurs.siren}
          >
            <Input
              id="ouv-siren"
              mono
              inputMode="numeric"
              value={siren}
              onChange={(evenement) => setSiren(evenement.target.value)}
              {...champ('ouv-siren', true, 'siren')}
            />
          </Champ>
          <Champ
            id="ouv-siret"
            label={t('ouvertures.galerie.siret')}
            aide={t('ouvertures.galerie.siretAide')}
            erreur={erreurs.siret}
          >
            <Input
              id="ouv-siret"
              mono
              inputMode="numeric"
              value={siret}
              onChange={(evenement) => setSiret(evenement.target.value)}
              {...champ('ouv-siret', true, 'siret')}
            />
          </Champ>
          <Champ
            id="ouv-tva"
            label={t('ouvertures.galerie.tva')}
            aide={t('ouvertures.galerie.tvaAide')}
            erreur={erreurs.tva}
            large
          >
            <Input
              id="ouv-tva"
              mono
              value={tva}
              onChange={(evenement) => setTva(evenement.target.value.toUpperCase())}
              {...champ('ouv-tva', true, 'tva')}
            />
          </Champ>
        </div>

        <h3>{t('ouvertures.galerie.adresse')}</h3>
        <div className={styles.groupeGrille}>
          <Champ id="ouv-ligne1" label={t('ouvertures.galerie.ligne1')} large>
            <Input
              id="ouv-ligne1"
              autoComplete="address-line1"
              value={ligne1}
              onChange={(evenement) => setLigne1(evenement.target.value)}
            />
          </Champ>
          <Champ id="ouv-ligne2" label={t('ouvertures.galerie.ligne2')} large>
            <Input
              id="ouv-ligne2"
              autoComplete="address-line2"
              value={ligne2}
              onChange={(evenement) => setLigne2(evenement.target.value)}
            />
          </Champ>
          <Champ id="ouv-cp" label={t('ouvertures.galerie.codePostal')}>
            <Input
              id="ouv-cp"
              autoComplete="postal-code"
              value={codePostal}
              onChange={(evenement) => setCodePostal(evenement.target.value)}
            />
          </Champ>
          <Champ id="ouv-ville" label={t('ouvertures.galerie.ville')}>
            <Input
              id="ouv-ville"
              autoComplete="address-level2"
              value={ville}
              onChange={(evenement) => setVille(evenement.target.value)}
            />
          </Champ>
          <Champ id="ouv-pays" label={t('ouvertures.galerie.pays')} erreur={erreurs.pays}>
            <Input
              id="ouv-pays"
              mono
              maxLength={2}
              value={pays}
              onChange={(evenement) => setPays(evenement.target.value.toUpperCase())}
              {...champ('ouv-pays', false, 'pays')}
            />
          </Champ>
        </div>

        <hr className={styles.separateur} />

        <h3>{t('ouvertures.galerie.reglages')}</h3>
        <div className={styles.groupeGrille}>
          <Champ id="ouv-langue" label={t('ouvertures.galerie.langue')}>
            <Select
              id="ouv-langue"
              value={langue}
              onValueChange={(valeur) => setLangue(valeur as 'fr' | 'en')}
              options={[
                { value: 'fr', label: t('ouvertures.galerie.langues.fr') },
                { value: 'en', label: t('ouvertures.galerie.langues.en') },
              ]}
            />
          </Champ>
          <Champ id="ouv-devise" label={t('ouvertures.galerie.devise')} erreur={erreurs.devise}>
            <Select
              id="ouv-devise"
              value={devise}
              onValueChange={setDevise}
              options={devisesProposees(devise).map((valeur) => ({ value: valeur, label: valeur }))}
            />
          </Champ>
          <Champ id="ouv-fuseau" label={t('ouvertures.galerie.fuseau')}>
            <Select
              id="ouv-fuseau"
              value={fuseau}
              onValueChange={setFuseau}
              options={fuseauxProposes(fuseau).map((valeur) => ({ value: valeur, label: valeur }))}
            />
          </Champ>
          <Champ
            id="ouv-groupe"
            label={t('ouvertures.galerie.groupe')}
            aide={t('ouvertures.galerie.groupeAide')}
          >
            <Input
              id="ouv-groupe"
              mono
              value={groupe}
              onChange={(evenement) => setGroupe(evenement.target.value)}
              {...champ('ouv-groupe', true)}
            />
          </Champ>
        </div>

        <div className={formStyles.champ}>
          <Checkbox
            id="ouv-test"
            label={t('ouvertures.galerie.estTest')}
            checked={estTest}
            onCheckedChange={(valeur) => setEstTest(valeur === true)}
            aria-describedby="ouv-test-aide"
          />
          <p id="ouv-test-aide" className={formStyles.aide}>
            {t('ouvertures.galerie.estTestAide')}
          </p>
        </div>

        {estTest ? (
          <>
            <p className={styles.info}>{t('ouvertures.assistant.test.info')}</p>
            <h3>{t('ouvertures.assistant.test.direction')}</h3>
            <p className={styles.groupeTexte}>{t('ouvertures.assistant.test.directionAide')}</p>
            <div className={styles.groupeGrille}>
              <Champ
                id="ouv-test-nom"
                label={t('ouvertures.equipe.nom')}
                erreur={erreurs.nomDirection}
              >
                <Input
                  id="ouv-test-nom"
                  value={nomDirection}
                  onChange={(evenement) => setNomDirection(evenement.target.value)}
                  {...champ('ouv-test-nom', false, 'nomDirection')}
                />
              </Champ>
              <Champ
                id="ouv-test-email"
                label={t('ouvertures.equipe.email')}
                erreur={erreurs.emailDirection}
              >
                <Input
                  id="ouv-test-email"
                  type="email"
                  autoComplete="off"
                  value={emailDirection}
                  onChange={(evenement) => setEmailDirection(evenement.target.value)}
                  {...champ('ouv-test-email', false, 'emailDirection')}
                />
              </Champ>
            </div>
          </>
        ) : null}
      </fieldset>

      <div className={styles.actionsEtape}>
        <span />
        <div>
          <Button type="submit" loading={enCours} disabled={lectureSeule}>
            {enCours
              ? t('ouvertures.assistant.enregistrement')
              : t('ouvertures.assistant.continuer')}
          </Button>
        </div>
      </div>
    </form>
  );
}
